import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Platform, Pressable, TouchableOpacity, View, useWindowDimensions } from 'react-native';
import moment from 'moment';
import { useUserContext } from '../../../common/utils/context';
import Picker from '../../components/picker/Picker';
import Text from '../../components/text/Text';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { DATE_PICKER_STYLE } from '../../utils/datePickerConstants';
import { createStyles } from './PlanogramItemExpiryDatePicker.styles';

let DateTimePickerModal;
if (Platform.OS === 'web') {
	DateTimePickerModal = require('../../components/DateTimePickerModal.web').default;
}
else {
	DateTimePickerModal = require('react-native-modal-datetime-picker').default;
}

const SELECTION_STEPS = {
	YEAR: 'year',
	MONTH: 'month',
	DAY: 'day',
};

const PlanogramItemExpiryDatePicker = ({
	planogramItem,
	setPlanogramItemValue,
	setExpiryDateItemKey,
	expiryDateItemKey,
	setExpiryDateSelectorDate,
	expiryDateSelectorDate,
}) => {
	// Theme-aware styles keep the calendar readable in dark mode.
	const styles = useThemedStyles(createStyles);
	const [showDatePicker, setShowDatePicker] = useState(false);
	// Step through year -> month -> day when the stepped style is selected.
	const [selectionStep, setSelectionStep] = useState(SELECTION_STEPS.YEAR);
	const [selectedYear, setSelectedYear] = useState(() => moment(expiryDateSelectorDate).year());
	const [selectedMonth, setSelectedMonth] = useState(() => moment(expiryDateSelectorDate).month());
	const actionSheetIgnoreUntilRef = useRef(0);
	const isActionSheetOpenRef = useRef(false);
	const { width: windowWidth } = useWindowDimensions();
	const isCompactLayout = windowWidth < 360;
	const monthPickerWidth = isCompactLayout ? '100%' : 140;
	const yearPickerWidth = isCompactLayout ? '100%' : 110;
	const { user } = useUserContext();
	const datePickerStyle = user?.settings?.datePickerStyle || DATE_PICKER_STYLE.STEPPED;
	const isNativePicker = datePickerStyle === DATE_PICKER_STYLE.NATIVE;
	const isSteppedPicker = datePickerStyle === DATE_PICKER_STYLE.STEPPED;
	const isClassicPicker = datePickerStyle === DATE_PICKER_STYLE.CLASSIC;
	const activeStep = isSteppedPicker ? selectionStep : SELECTION_STEPS.DAY;

	const endExpirySelection = useCallback(() => {
		setExpiryDateItemKey(null);
	}, [setExpiryDateItemKey]);

	// On iOS, clearing the selection key reopens the hosting edit dialog, so it
	// must wait until this picker's modal has fully dismissed (onDismiss/onHide);
	// overlapping native modal transitions freeze the app. Web also waits for
	// onHide, so the date sheet slides away instead of being unmounted at once.
	const dismissPicker = useCallback(() => {
		setShowDatePicker(false);
		if (Platform.OS === 'android') {
			endExpirySelection();
		}
	}, [endExpirySelection]);

	const closePicker = useCallback(() => {
		dismissPicker();
		setExpiryDateSelectorDate(moment());
	}, [dismissPicker, setExpiryDateSelectorDate]);

	const handleActionSheetOpen = useCallback(() => {
		isActionSheetOpenRef.current = true;
	}, []);

	const handleActionSheetClose = useCallback(() => {
		isActionSheetOpenRef.current = false;
		// Avoid closing the dialog when the action sheet is dismissed by tapping outside.
		actionSheetIgnoreUntilRef.current = Date.now() + 300;
	}, []);

	const handleOverlayPress = useCallback(() => {
		if (isActionSheetOpenRef.current || Date.now() < actionSheetIgnoreUntilRef.current) {
			return;
		}
		closePicker();
	}, [closePicker]);

	const handleBackStep = useCallback(() => {
		setSelectionStep((current) => {
			if (current === SELECTION_STEPS.DAY) {
				return SELECTION_STEPS.MONTH;
			}
			if (current === SELECTION_STEPS.MONTH) {
				return SELECTION_STEPS.YEAR;
			}
			return current;
		});
	}, []);

	const handleNextStep = useCallback(() => {
		setSelectionStep((current) => {
			if (current === SELECTION_STEPS.YEAR) {
				return SELECTION_STEPS.MONTH;
			}
			if (current === SELECTION_STEPS.MONTH) {
				return SELECTION_STEPS.DAY;
			}
			return current;
		});
	}, []);

	// Build month and year options so users can jump directly instead of tapping arrows many times
	const monthOptions = useMemo(() => ([
		{ label: 'January', value: 0 },
		{ label: 'February', value: 1 },
		{ label: 'March', value: 2 },
		{ label: 'April', value: 3 },
		{ label: 'May', value: 4 },
		{ label: 'June', value: 5 },
		{ label: 'July', value: 6 },
		{ label: 'August', value: 7 },
		{ label: 'September', value: 8 },
		{ label: 'October', value: 9 },
		{ label: 'November', value: 10 },
		{ label: 'December', value: 11 },
	]), []);

	const yearOptions = useMemo(() => {
		const todayYear = moment().year();
		const currentYear = selectedYear;

		// Always include current calendar year and a reasonable future range
		const minYear = Math.min(todayYear, currentYear);
		const maxYear = Math.max(todayYear + 10, currentYear);

		const options = [];
		for (let year = minYear; year <= maxYear; year += 1) {
			options.push({ label: `${year}`, value: year });
		}
		return options;
	}, [selectedYear]);

	const handleMonthChange = useCallback((newMonth) => {
		setSelectedMonth(newMonth);
		if (isSteppedPicker) {
			setSelectionStep(SELECTION_STEPS.DAY);
		}
	}, [isSteppedPicker]);

	const handleYearChange = useCallback((newYear) => {
		setSelectedYear(newYear);
		if (isSteppedPicker) {
			setSelectionStep(SELECTION_STEPS.MONTH);
		}
	}, [isSteppedPicker]);

	const onChange = useCallback((selectedDate) => {

		const newDate = moment(selectedDate).hours(12);
		const today = moment().startOf('day');

		// cancel if date is before today
		if (newDate.isBefore(today)) {
			dismissPicker();
			return;
		}

		setPlanogramItemValue(planogramItem.id, 'expiryDates')({ selection: expiryDateItemKey, date: newDate });
		dismissPicker();
	}, [dismissPicker, expiryDateItemKey, planogramItem.id, setPlanogramItemValue]);

	const pickerDate = useMemo(() => {
		return moment(expiryDateSelectorDate).isBefore(moment()) ? new Date() : new Date(expiryDateSelectorDate);
	}, [expiryDateSelectorDate]);

	// delay the dialog from showing until the other (planogram item edit) dialog is closed as iPhone doesn't allow two modals to be open at the same time
	useEffect(() => {
		if (Platform.OS === 'ios') {
			setTimeout(() => {
				setShowDatePicker(Boolean(expiryDateItemKey));
			}, 500);
		}
		else {
			setShowDatePicker(Boolean(expiryDateItemKey));
		}
	}, [expiryDateItemKey]);

	useEffect(() => {
		const nextDate = moment(expiryDateSelectorDate);
		setSelectedYear(nextDate.year());
		setSelectedMonth(nextDate.month());
	}, [expiryDateSelectorDate]);

	useEffect(() => {
		if (expiryDateItemKey) {
			setSelectionStep(isSteppedPicker ? SELECTION_STEPS.YEAR : SELECTION_STEPS.DAY);
		}
	}, [expiryDateItemKey, isSteppedPicker]);

	const calendarMonth = useMemo(() => (
		moment()
			.year(selectedYear)
			.month(selectedMonth)
			.startOf('month')
	), [selectedYear, selectedMonth]);

	const weeks = useMemo(() => {
		if (activeStep !== SELECTION_STEPS.DAY) {
			return [];
		}
		// Build a simple month grid so users can jump straight to a day without waiting for a spinner
		const start = calendarMonth.clone().startOf('month').startOf('week');
		const end = calendarMonth.clone().endOf('month').endOf('week');
		const days = [];
		const cursor = start.clone();
		while (cursor.isSameOrBefore(end, 'day')) {
			days.push(cursor.clone());
			cursor.add(1, 'day');
		}
		return days.reduce((acc, day, index) => {
			const weekIndex = Math.floor(index / 7);
			if (!acc[weekIndex]) {
				acc[weekIndex] = [];
			}
			acc[weekIndex].push(day);
			return acc;
		}, []);
	}, [activeStep, calendarMonth]);

	const selectedDate = useMemo(() => moment(expiryDateSelectorDate).startOf('day'), [expiryDateSelectorDate]);
	const today = useMemo(() => moment().startOf('day'), []);
	const selectedMonthLabel = useMemo(() => (
		monthOptions.find(option => option.value === selectedMonth)?.label || 'Month'
	), [monthOptions, selectedMonth]);
	const stepTitle = useMemo(() => {
		if (selectionStep === SELECTION_STEPS.MONTH) {
			return 'Select month';
		}
		if (selectionStep === SELECTION_STEPS.DAY) {
			return 'Select day';
		}
		return 'Select year';
	}, [selectionStep]);
	const shouldShowBack = isSteppedPicker && selectionStep !== SELECTION_STEPS.YEAR;
	const shouldShowNext = isSteppedPicker && selectionStep !== SELECTION_STEPS.DAY;
	const shouldUseSystemPicker = Platform.OS === 'web' || isNativePicker;
	const disableOutsideMonth = isSteppedPicker;

	const renderCalendarPicker = () => (
		<Modal
			visible={showDatePicker}
			transparent={true}
			animationType="fade"
			onDismiss={endExpirySelection}
			onRequestClose={() => {
				closePicker();
			}}
		>
			<View style={styles.overlay}>
				<Pressable
					style={styles.overlayPressable}
					onPress={handleOverlayPress}
				/>
				<View style={styles.calendarCard}>
					{isSteppedPicker && (
						<>
							<View style={styles.stepHeader}>
								{
									shouldShowBack ? (
										<TouchableOpacity
											style={styles.backButton}
											onPress={handleBackStep}
										>
											<Text style={styles.backText}>Back</Text>
										</TouchableOpacity>
									) : <View style={styles.headerSpacer} />
								}
								<Text style={styles.stepTitle}>{stepTitle}</Text>
								{
									shouldShowNext ? (
										<TouchableOpacity
											style={styles.nextButton}
											onPress={handleNextStep}
										>
											<Text style={styles.nextText}>Next</Text>
										</TouchableOpacity>
									) : <View style={styles.headerSpacer} />
								}
							</View>

							{selectionStep === SELECTION_STEPS.YEAR && (
								<View style={styles.stepContent}>
									{/* Show quick-pick buttons so users don't have to open dropdowns. */}
									<View style={styles.optionGrid}>
										{yearOptions.map(option => {
											const isSelected = option.value === selectedYear;
											return (
												<TouchableOpacity
													key={option.value}
													style={[
														styles.optionButton,
														isSelected && styles.optionButtonSelected,
													]}
													onPress={() => handleYearChange(option.value)}
												>
													<Text
														numberOfLines={1}
														style={[
															styles.optionText,
															isSelected && styles.optionTextSelected,
														]}
													>
														{option.label}
													</Text>
												</TouchableOpacity>
											);
										})}
									</View>
								</View>
							)}

							{selectionStep === SELECTION_STEPS.MONTH && (
								<View style={styles.stepContent}>
									<View style={styles.optionGrid}>
										{monthOptions.map(option => {
											const isSelected = option.value === selectedMonth;
											return (
												<TouchableOpacity
													key={option.value}
													style={[
														styles.optionButton,
														isSelected && styles.optionButtonSelected,
													]}
													onPress={() => handleMonthChange(option.value)}
												>
													<Text
														numberOfLines={1}
														style={[
															styles.optionText,
															isSelected && styles.optionTextSelected,
														]}
													>
														{option.label}
													</Text>
												</TouchableOpacity>
											);
										})}
									</View>
									<Text style={styles.stepSubheading}>Year: {selectedYear}</Text>
								</View>
							)}
						</>
					)}

					{isClassicPicker && (
						<View style={[styles.classicHeader, isCompactLayout && styles.classicHeaderCompact]}>
							<View style={[styles.classicHeaderColumn, isCompactLayout && styles.classicHeaderColumnCompact]}>
								<Text style={styles.headerLabel}>Month</Text>
								<Picker
									options={monthOptions}
									selectedValue={selectedMonth}
									onValueChange={handleMonthChange}
									width={monthPickerWidth}
									onActionSheetOpen={handleActionSheetOpen}
									onActionSheetClose={handleActionSheetClose}
								/>
							</View>
							<View style={[
								styles.classicHeaderColumn,
								isCompactLayout && styles.classicHeaderColumnCompact,
								isCompactLayout && styles.classicHeaderColumnCompactLast,
							]}
							>
								<Text style={styles.headerLabel}>Year</Text>
								<Picker
									options={yearOptions}
									selectedValue={selectedYear}
									onValueChange={handleYearChange}
									width={yearPickerWidth}
									onActionSheetOpen={handleActionSheetOpen}
									onActionSheetClose={handleActionSheetClose}
								/>
							</View>
						</View>
					)}

					{activeStep === SELECTION_STEPS.DAY && (
						<>
							{isSteppedPicker && (
								<Text style={styles.stepSubheading}>{selectedMonthLabel} {selectedYear}</Text>
							)}
							<View style={styles.weekdayRow}>
								{['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, index) => (
									<Text key={index} style={styles.weekdayText}>{d}</Text>
								))}
							</View>

							{weeks.map((week, i) => (
								<View key={i} style={styles.weekRow}>
									{week.map(day => {
										const isOutsideMonth = disableOutsideMonth && !day.isSame(calendarMonth, 'month');
										const isPast = day.isBefore(today, 'day');
										const isDisabled = isOutsideMonth || isPast;
										const isSelected = !isDisabled && selectedDate.isSame(day, 'day');
										return (
											<TouchableOpacity
												key={day.format('YYYY-MM-DD')}
												disabled={isDisabled}
												style={[
													styles.dayCell,
													isSelected && styles.daySelected,
													isDisabled && styles.dayDisabled,
												]}
												onPress={() => onChange(day.toDate())}
											>
												<Text style={[
													styles.dayText,
													isSelected && styles.dayTextSelected,
													isDisabled && styles.dayTextDisabled,
												]}
												>
													{day.date()}
												</Text>
											</TouchableOpacity>
										);
									})}
								</View>
							))}
						</>
					)}

					<TouchableOpacity
						style={styles.cancelButton}
						onPress={closePicker}
					>
						<Text style={styles.cancelText}>Cancel</Text>
					</TouchableOpacity>
				</View>
			</View>
		</Modal>
	);

	return (
		// The hosts mount this component only while a selection key is set; the
		// modals stay mounted through their exit animation so the dismiss
		// callbacks above can fire before the key clears and unmounts them.
		<View>
			{
				// Web keeps the existing dedicated web picker for consistency. Native can opt into the system picker.
				shouldUseSystemPicker
					? (
						<DateTimePickerModal
							isVisible={showDatePicker}
							mode="date"
							onConfirm={onChange}
							onCancel={closePicker}
							onHide={endExpirySelection}
							date={pickerDate}
							minimumDate={new Date()}
						/>
					)
					: renderCalendarPicker()
			}
		</View>
	);
};

export default PlanogramItemExpiryDatePicker;
